import { useMutation, queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import {
    AgreementDocumentSchema,
    CustomerDocumentSchema,
    DOC_SEGMENTS,
    DocsFilteringDataSchema,
    DocumentDetailsArrayResponseSchema,
    DocumentDetailsSchema,
    DocumentDownloadResponseSchema,
    GenerateDocumentNumberResponseSchema,
    MassActionRequestSchema,
    MultipleFileUploadTokenRequestSchema,
    MultipleFileUploadTokenResponseSchema,
    ProductDocumentSchema,
    isFullDocumentDetails,
} from "../schemas/documents";

const getUploadTokensForFiles = async (axios, files) => {
    const tokenRequests = MultipleFileUploadTokenRequestSchema.parse(
        files.map((fileData) => ({
            file_ori_name: fileData.file.name,
            content_type: fileData.file.type,
            unique_index: fileData.unique_index,
        })),
    );

    return await axios
        .post(apiUrls.files.uploadToken, tokenRequests)
        .then((res) => MultipleFileUploadTokenResponseSchema.parse(res.data));
};

const uploadFilesToAzure = async (uploadTokens, files) => {
    await Promise.all(
        uploadTokens.map((uploadToken) => {
            const fileData = files.find((f) => f.unique_index === uploadToken.unique_index);
            if (!fileData) {
                throw new Error(`Could not find file for unique_index ${uploadToken.unique_index}`);
            }
            return documentsApi.uploadFileToAzure(fileData.file, uploadToken.upload_url);
        }),
    );
};

const registerDocumentsInHome = async (axios, uploadTokens, files, commonData, documentSegment, docsNumber) => {
    const docs = await Promise.all(
        uploadTokens.map(async (token) => {
            const fileData = files.find((f) => f.unique_index === token.unique_index);

            const docNumberResponse = await documentsApi.generateDocumentNumber(
                axios,
                documentSegment,
            );

            const doc = {
                content_type: fileData?.file.type || "",
                file_ori_name: token.file_ori_name,
                file_name: token.file_name,
                docs_name: fileData?.docs_name || token.file_ori_name,
                docs_nr: docNumberResponse?.document_number || docsNumber,
                revision_parent: commonData.revision_parent,
                industry: commonData.industry,
            };

            if (commonData.docs_restricted_user && commonData.docs_restricted_user.length > 0) {
                doc.docs_restricted_user = commonData.docs_restricted_user
                    .map((user) => (typeof user === "string" ? user : user?.id))
                    .filter((id) => id != null);
            }

            if (commonData.docs_restricted_role && commonData.docs_restricted_role.length > 0) {
                doc.docs_restricted_role = commonData.docs_restricted_role
                    .map((role) => (typeof role === "string" ? role : role?.id))
                    .filter((id) => id != null);
            }

            return doc;
        }),
    );
    const createDocsRequest = MassActionRequestSchema.parse({ docs });
    return await documentsApi.registerDocuments(axios, createDocsRequest);
};

async function uploadFilesForProducts(axios, params) {
    const { files, product_ids, commonData } = params;

    const uploadTokens = await getUploadTokensForFiles(axios, files);
    await uploadFilesToAzure(uploadTokens, files);
    const createdDocIds = await registerDocumentsInHome(
        axios,
        uploadTokens,
        files,
        commonData,
        DOC_SEGMENTS.PRODUCT,
    );

    // Update documents with industry and connect to products
    await Promise.all(
        createdDocIds.map(async (docId) => {
            // Connect to products
            if (product_ids && product_ids.length > 0) {
                await documentsApi.connectDocumentToProducts(axios, {
                    document_id: docId,
                    product_ids: product_ids,
                    note: commonData.note,
                    is_card_jpg: commonData.is_card_jpg,
                    is_card_pdf: commonData.is_card_pdf,
                });
            }
        }),
    );
    return createdDocIds;
}

async function uploadFilesForCustomer(axios, params) {
    const { files, customer_id, commonData } = params;

    const uploadTokens = await getUploadTokensForFiles(axios, files);
    await uploadFilesToAzure(uploadTokens, files);
    const createdDocIds = await registerDocumentsInHome(
        axios,
        uploadTokens,
        files,
        commonData,
        DOC_SEGMENTS.CUSTOMER,
    );

    // Update documents with industry and connect to customer
    await Promise.all(
        createdDocIds.map(async (docId) => {
            // Connect to customer
            if (customer_id) {
                await documentsApi.connectDocumentToCustomer(axios, {
                    docscust_docs: docId,
                    docscust_customer: customer_id,
                    note: commonData.note,
                });
            }
        }),
    );
    return createdDocIds;
}

async function uploadFilesForInvests(axios, params) {
    const { files, invest_ids, commonData } = params;

    const uploadTokens = await getUploadTokensForFiles(axios, files);
    await uploadFilesToAzure(uploadTokens, files);
    const createdDocIds = await registerDocumentsInHome(
        axios,
        uploadTokens,
        files,
        commonData,
        DOC_SEGMENTS.INVESTMENT,
    );

    // Update documents with industry and connect to products
    await Promise.all(
        createdDocIds.map(async (docId) => {
            if (invest_ids && invest_ids.length > 0) {
                await documentsApi.connectDocumentToInvests(axios, {
                    document_id: docId,
                    invest_ids: invest_ids,
                    note: commonData.note,
                    is_prospectus: commonData.is_prospectus,
                });
            }
        }),
    );
    return createdDocIds;
}

async function uploadFilesForAgreement(axios, params) {
    const { files, agreement_id, commonData } = params;

    const uploadTokens = await getUploadTokensForFiles(axios, files);
    await uploadFilesToAzure(uploadTokens, files);
    const createdDocIds = await registerDocumentsInHome(
        axios,
        uploadTokens,
        files,
        commonData,
        DOC_SEGMENTS.AGREEMENT,
    );

    // Connect documents to agreement
    await Promise.all(
        createdDocIds.map(async (docId) => {
            if (agreement_id) {
                await documentsApi.connectDocumentToAgreement(axios, {
                    docsagre_docs: docId,
                    docsagre_agreement: agreement_id,
                    note: commonData.note,
                });
            }
        }),
    );
    return createdDocIds;
}

async function uploadFilesForChannel(axios, params) {
    const { files, channel_id, commonData } = params;

    // 1. Get upload tokens for all files
    const tokenRequests = files.map((fileData) => ({
        file_ori_name: fileData.file.name,
        content_type: fileData.file.type,
        unique_index: fileData.unique_index,
    }));

    const tokenResponses = await axios
        .post(apiUrls.files.uploadToken, tokenRequests)
        .then((res) => MultipleFileUploadTokenResponseSchema.parse(res.data));

    // 2. Upload files to Azure
    await Promise.all(
        tokenResponses.map((tokenResponse) => {
            const fileData = files.find((f) => f.unique_index === tokenResponse.unique_index);
            if (!fileData) {
                throw new Error(
                    `Could not find file for unique_index ${tokenResponse.unique_index}`,
                );
            }
            return documentsApi.uploadFileToAzure(fileData.file, tokenResponse.upload_url);
        }),
    );

    // 3. Register documents in Home (mass-action)
    const createDocRequests = {
        docs: await Promise.all(
            tokenResponses.map(async (token, index) => {
                const fileData = files.find((f) => f.unique_index === token.unique_index);

                // Generate document number from API
                const docNumberResponse = await documentsApi.generateDocumentNumber(
                    axios,
                    DOC_SEGMENTS.CHANNEL,
                );

                const doc = {
                    content_type: fileData?.file.type || "",
                    file_ori_name: token.file_ori_name,
                    file_name: token.file_name,
                    docs_name: fileData?.docs_name || token.file_ori_name,
                    docs_nr: docNumberResponse.document_number,
                    revision_parent: commonData.revision_parent,
                };

                if (commonData.docs_restricted_user && commonData.docs_restricted_user.length > 0) {
                    doc.docs_restricted_user = commonData.docs_restricted_user
                        .map((user) => {
                            return typeof user === "string" ? user : user?.id;
                        })
                        .filter((id) => id != null);
                }

                if (commonData.docs_restricted_role && commonData.docs_restricted_role.length > 0) {
                    doc.docs_restricted_role = commonData.docs_restricted_role
                        .map((role) => {
                            return typeof role === "string" ? role : role?.id;
                        })
                        .filter((id) => id != null);
                }

                return doc;
            }),
        ),
    };

    const createdDocIds = await documentsApi.registerDocuments(axios, createDocRequests);

    // 4. Update documents with industry and connect to channel
    await Promise.all(
        createdDocIds.map(async (docId) => {
            // 4b. Connect to channel
            if (channel_id) {
                await documentsApi.connectDocumentToChannel(axios, {
                    docschan_docs: docId,
                    docschan_channel: channel_id,
                    docschan_note: commonData.note,
                });
            }
        }),
    );
    return createdDocIds;
}

async function uploadOnlyFiles(axios, params) {
    const { files, commonData } = params;

    const uploadTokens = await getUploadTokensForFiles(axios, files);
    await uploadFilesToAzure(uploadTokens, files);
    const createdDocIds = await registerDocumentsInHome(
        axios,
        uploadTokens,
        files,
        commonData,
        null,
        params.docs_nr
    );
    return createdDocIds;
}

export const documentsApi = {
    getUploadToken: async (axios, data) => {
        return axios
            .post(apiUrls.files.uploadToken, data)
            .then((response) => MultipleFileUploadTokenResponseSchema.parse(response.data));
    },

    uploadFileToAzure: async (file, uploadUrl) => {
        return fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
                "Content-Type": file.type,
                "x-ms-blob-type": "BlockBlob",
            },
        }).then((response) => {
            if (!response.ok) {
                throw new Error(`Upload failed: ${response.statusText}`);
            }
        });
    },

    uploadThumbnailToAzure: async (thumbnail, uploadUrl) => {
        return fetch(uploadUrl, {
            method: "PUT",
            body: thumbnail,
            headers: {
                "Content-Type": thumbnail.type,
                "x-ms-blob-type": "BlockBlob",
            },
        }).then((response) => {
            if (!response.ok) {
                throw new Error(`Thumbnail upload failed: ${response.statusText}`);
            }
        });
    },

    registerDocuments: async (axios, data) => {
        return axios.post(apiUrls.files.massActionHome, data).then((response) => {
            const data = response.data;                
            return data.map((item) => item.id);
        });
    },

    generateDocumentNumber: async (axios, segment) => {
        if (!segment) {
            return Promise.resolve({}) 
        }
        return axios
            .post(apiUrls.files.generateDocumentNumber, { segment })
            .then((response) => GenerateDocumentNumberResponseSchema.parse(response.data));
    },

    connectDocumentToCustomer: async (axios, data) => {
        return axios.post(apiUrls.files.connectToCustomer, data).then((response) => response.data);
    },

    connectDocumentToProduct: async (axios, data) => {
        return axios.post(apiUrls.files.connectToProduct, data).then((response) => response.data);
    },

    connectDocumentToInvests: async (axios, data) => {
        return axios
            .put(apiUrls.files.updateDocumentInvests, data)
            .then((response) => response.data);
    },

    connectDocumentToAgreement: async (axios, data) => {
        return axios.post(apiUrls.files.connectToAgreement, data).then((response) => response.data);
    },

    connectDocumentToProducts: async (axios, data) => {
        return axios
            .put(apiUrls.files.updateDocumentProducts, data)
            .then((response) => response.data);
    },

    connectDocumentToInvests: async (axios, data) => {
        return axios
            .put(apiUrls.files.updateDocumentInvests, data)
            .then((response) => response.data);
    },

    connectDocumentToChannel: async (axios, data) => {
        return axios.post(apiUrls.files.connectToChannel, data).then((response) => response.data);
    },

    // Listing functions
    listCustomerDocuments: async (axios, customerId) => {
        return axios
            .get(apiUrls.customers.listCustomerDocuments(customerId))
            .then((response) => z.array(CustomerDocumentSchema).parse(response.data));
    },

    listProductDocuments: async (axios, productId, params) => {
        const searchParams = params
            ? new URLSearchParams(
                  Object.entries(params).map(([key, value]) => [key, String(value)]),
              )
            : undefined;

        return axios
            .get(apiUrls.products.listProductDocuments(productId), { params: searchParams })
            .then((response) => {
                const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
                // Filter out documents that don't have full details
                return parsedResponse.filter(isFullDocumentDetails);
            });
    },

    cardsProductDocuments: async (axios, productId) => {
        return axios.get(apiUrls.products.cardsProductDocuments(productId)).then((response) => {
            const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
            // Filter out documents that don't have full details
            return parsedResponse.filter(isFullDocumentDetails);
        });
    },

    listDocumentProducts: async (axios, documentId) => {
        return axios
            .get(apiUrls.documents.listDocumentProducts(documentId))
            .then((response) => z.array(ProductDocumentSchema).parse(response.data));
    },

    listAgreementDocuments: async (axios, agreementId) => {
        return axios
            .get(apiUrls.agreements.listAgreementDocuments(agreementId))
            .then((response) => z.array(AgreementDocumentSchema).parse(response.data));
    },

    getAllProductDocumentsWithDetails: async (axios, params) => {
        return axios.get(apiUrls.files.listAllProductDocuments, { params }).then((response) => {
            const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
            return parsedResponse.filter(isFullDocumentDetails);
        });
    },

    getAllInvestmentDocumentsWithDetails: async (axios, params) => {
        return axios
            .get(apiUrls.files.listAllInvestmentDocuments, { params })
            .then((response) => z.array(DocumentDetailsSchema).parse(response.data));
    },

    deleteDocumentFromProduct: async (axios, data) => {
        return axios
            .delete(apiUrls.products.deleteFromProduct, { data })
            .then((response) => response.data);
    },

    getDocumentDetails: async (axios, data) => {
        return axios.post(apiUrls.files.details, data).then((response) => {
            const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
            return parsedResponse.filter(isFullDocumentDetails);
        });
    },

    getSingleDocumentDetails: async (axios, documentId, docsType) => {
        return await axios.post(apiUrls.documents.details(documentId), {docs_type: docsType}).then((response) => response.data)
    },

    getDocumentRevisionHistory: async (axios, documentId) => {
        return await axios.post(apiUrls.documents.revisionHistory(documentId)).then((response) => response.data)
    },

    updateDocument: async (axios, documentId, updatedData) => {
        return await axios
            .patch(apiUrls.documents.update(documentId), updatedData )
            .then((response) => response.data);
    },


    getCustomerDocumentsWithDetails: async (axios, customerId) => {
        const customerDocuments = await documentsApi.listCustomerDocuments(axios, customerId);

        if (customerDocuments.length === 0) {
            return [];
        }
        const documentIds = customerDocuments.map((doc) => doc.docscust_docs);
        const documentDetails = await documentsApi.getDocumentDetails(axios, {
            docs: documentIds,
        });

        return customerDocuments
            .map((customerDoc) => {
                const details = documentDetails.find(
                    (detail) => detail.id === customerDoc.docscust_docs,
                );
                if (!details) {
                    return undefined;
                }
                return {
                    ...customerDoc,
                    details,
                };
            })
            .filter((doc) => doc !== undefined);
    },

    getAgreementDocumentsWithDetails: async (axios, agreementId) => {
        const agreementDocuments = await documentsApi.listAgreementDocuments(axios, agreementId);

        if (agreementDocuments.length === 0) {
            return [];
        }
        const documentIds = agreementDocuments.map((doc) => doc.docsagre_docs);
        const documentDetails = await documentsApi.getDocumentDetails(axios, {
            docs: documentIds,
        });

        return agreementDocuments
            .map((agreementDoc) => {
                const details = documentDetails.find(
                    (detail) => detail.id === agreementDoc.docsagre_docs,
                );
                if (!details) {
                    return undefined;
                }
                return {
                    ...agreementDoc,
                    details,
                };
            })
            .filter((doc) => doc !== undefined);
    },

    getDocumentDownloadLink: async (axios, documentId) => {
        return axios
            .get(apiUrls.documents.download(documentId))
            .then((response) => DocumentDownloadResponseSchema.parse(response.data));
    },

    getBlobFromAzureByUrl: async (downloadUrl) => {
        return fetch(downloadUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch blob: ${response.statusText}`);
                }
                return response.blob();
            })
            .then((blob) => {
                return URL.createObjectURL(blob);
            });
    },

    handleDownloadDocumentFromLink: async (axios, documentId) => {
        const { download_link, docs_name } = await documentsApi.getDocumentDownloadLink(axios, documentId);
        // wait for MyHome update to add docs_name
        if (!docs_name) {
            window.open(download_link, "_blank");
        } else {
            const blobUrl = await documentsApi.getBlobFromAzureByUrl(download_link)
            var a = document.createElement("a");
            document.body.appendChild(a);
            a.style = "display: none";
            a.href = blobUrl;
            a.download = docs_name;
            a.click();
        }
    },

    handlePreviewDocumentFromLink: async (axios, documentId) => {
        const { download_link: preview_link, content_type } =
            await documentsApi.getDocumentDownloadLink(axios, documentId);
        const blobUrl = await documentsApi.getBlobFromAzureByUrl(preview_link);
        const blob = await fetch(blobUrl).then((r) => r.blob());
        const file = new File([blob], "preview", { type: content_type });
        const fileUrl = URL.createObjectURL(file);
        window.open(fileUrl, "_blank");
    },

    updateDocumentProducts: async (axios, data) => {
        return axios
            .put(apiUrls.files.updateDocumentProducts, data)
            .then((response) => response.data);
    },

    deleteDocumentFromHome: async (axios, documentId) => {
        return axios
            .delete(`/files/home/docs/${documentId}/delete/`)
            .then((response) => response.data);
    },

    deleteDocumentFromAllProductsSales: async (axios, documentId) => {
        return axios
            .post(apiUrls.files.deleteAllFromProduct, { document_id: documentId })
            .then((response) => response.data);
    },

    deleteDocumentsFromCustomerSales: async (axios, data) => {
        return axios
            .delete(apiUrls.customers.deleteFromCustomer, { data })
            .then((response) => response.data);
    },

    deleteDocumentsFromAgreementSales: async (axios, data) => {
        return axios
            .delete(apiUrls.agreements.deleteFromAgreement, { data })
            .then((response) => response.data);
    },

    deleteDocumentFromAllInvestsSales: async (axios, documentId) => {
        return axios
            .post(apiUrls.files.deleteAllFromInvest, { document_id: documentId })
            .then((response) => response.data);
    },

    deleteDocumentsFromChannel: async (axios, data) => {
        return axios
            .delete(apiUrls.sales.channels.deleteDocumentFromChannel, { data })
            .then((response) => response.data);
    },
    
    uploadFilesForProducts,
    uploadFilesForCustomer,
    uploadFilesForAgreement,
    uploadFilesForInvests,
    uploadFilesForChannel,
    uploadOnlyFiles
};

export function useDocsFilteringData(options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["docs-filtering-data"],
        queryFn: () =>
            axiosPriv
                .get(apiUrls.files.docsFilteringData)
                .then((response) => response.data)
                .then((filteringData) => DocsFilteringDataSchema.parse(filteringData)),
        ...options,
    });
}

export function useGenerateDocumentNumber(segment, options = {}) {
    const axiosPriv = useAxiosPrivate();
    const { onServerError } = useFormServerSideErrorsHandler();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["generate-docs-number", segment],
        queryFn: () => {
            return documentsApi.generateDocumentNumber(axiosPriv, segment);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

async function createThumbnail(file, width, height) {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
            canvas.width = width;
            canvas.height = height;

            if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error("Failed to create thumbnail"));
                        }
                    },
                    "image/jpeg",
                    0.8,
                );
            } else {
                reject(new Error("Failed to get canvas context"));
            }
        };

        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = URL.createObjectURL(file);
    });
}

export function useUploadOnlyFiles(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.uploadOnlyFiles(axiosPriv, data, data.docs_nr),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["document-detail", variables.id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useUploadFilesForProducts(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.uploadFilesForProducts(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["all-product-documents-with-details"],
            });
            for (const productId of variables.product_ids) {
                await queryClient.invalidateQueries({
                    queryKey: ["product-documents-with-details", productId],
                });
            }
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useUploadFilesForCustomer(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.uploadFilesForCustomer(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["list-customer-documents", variables.customer_id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useUploadFilesForInvests(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.uploadFilesForInvests(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["all-invest-documents-with-details"],
            });
            for (const investId of variables.invest_ids) {
                await queryClient.invalidateQueries({
                    queryKey: ["invest-documents-with-details", investId],
                });
            }
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useUploadFilesForAgreement(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.uploadFilesForAgreement(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["list-agreement-documents", variables.agreement_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["agreement-documents-with-details", variables.agreement_id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useUploadFilesForChannel(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.uploadFilesForChannel(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["list-channel-documents", variables.channel_id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useDeleteDocumentFromAllProducts(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: async ({ document_id }) => {
            await documentsApi.deleteDocumentFromAllProductsSales(axiosPriv, document_id);
        },
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["all-product-documents-with-details"],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useDeleteDocumentFromAllInvests(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: async ({ document_id }) => {
            await documentsApi.deleteDocumentFromAllInvestsSales(axiosPriv, document_id);
        },
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["all-invest-documents-with-details"],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useCustomerDocuments(customerId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        queryKey: ["customer-documents", customerId],
        queryFn: () => documentsApi.listCustomerDocuments(axiosPriv, customerId),
        enabled: !!customerId,
        ...options,
    });
}

export function useProductDocuments(productId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        queryKey: ["product-documents", productId],
        queryFn: () => documentsApi.listProductDocuments(axiosPriv, productId),
        enabled: !!productId,
        ...options,
    });
}

export function useProductsDocuments(productIds, options = {}, params = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        ...options,
        queryKey: ["products-documents", ...productIds, params],
        queryFn: async () => {
            if (productIds.length === 0) {
                return [];
            }
            return Promise.all(
                productIds.map((id) => documentsApi.listProductDocuments(axiosPriv, id, params)),
            ).then((nestedResults) => {
                let docsIds = []
                let resData = []

                for (const results of nestedResults) {
                    for (const doscData of results) {
                        if (docsIds.includes(doscData.id)) {
                            continue
                        }
                        docsIds.push(doscData.id)
                        resData.push(doscData)   
                    }
                }
                return resData
                // to avoid dosc dupilactes
                // return nestedResults.flat()
            });
        },
        enabled: productIds.length > 0,
    });
}

export function useDocumentProducts(documentId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        queryKey: ["document-products", documentId],
        queryFn: () => documentsApi.listDocumentProducts(axiosPriv, documentId),
        enabled: !!documentId,
        ...options,
    });
}

export function useCustomerDocumentsWithDetails(customerId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["customer-documents-with-details", customerId],
        queryFn: () => documentsApi.getCustomerDocumentsWithDetails(axiosPriv, customerId),
        enabled: !!customerId,
        ...options,
    });
}

export function useAllProductDocumentsWithDetails(params = new URLSearchParams(), options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["all-product-documents-with-details", Object.fromEntries(params.entries())],
        queryFn: () => documentsApi.getAllProductDocumentsWithDetails(axiosPriv, params),
        ...options,
    });
}

export function useAllInvestmentDocumentsWithDetails(params = new URLSearchParams(), onlyProspectus=false, options = {}) {
    const searchParams = new URLSearchParams(params)
    if (onlyProspectus) {
        searchParams.set("docsinve_is_prospectus", true)
    }
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["all-invest-documents-with-details", Object.fromEntries(params.entries())],
        queryFn: () => documentsApi.getAllInvestmentDocumentsWithDetails(axiosPriv, searchParams),
        ...options,
    });
}

export function useDocumentDetails(documentIds, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        queryKey: ["document-details", documentIds],
        queryFn: () =>
            documentsApi.getDocumentDetails(axiosPriv, {
                docs: documentIds,
            }),
        enabled: documentIds.length > 0,
        ...options,
    });
}

export function useSingleDocumentDetails(documentId, docsType) {
    if (!documentId) {
        return Promise.resolve({});
    }

    const axiosPriv = useAxiosPrivate();

    return useQuery({
        queryKey: ["document-detail", documentId],
        queryFn: () => documentsApi.getSingleDocumentDetails(axiosPriv, documentId, docsType)
    });
}

export function useDocumentRevisionHistory(documentId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        queryKey: ["document-revision-history", documentId],
        queryFn: () =>
            documentsApi.getDocumentRevisionHistory(axiosPriv, documentId),
        enabled: !!documentId,
        ...options,
    });
}

export function useUpdateDocument(options = {}) {
const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: ({data:data, id: documentId}) => documentsApi.updateDocument(axiosPriv, documentId, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["document-products", variables.id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["document-detail", variables.id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useListProductDocuments(productId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["list-product-documents", productId],
        queryFn: () =>
            axiosPriv.get(apiUrls.products.listProductDocuments(productId)).then((response) => {
                const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
                return parsedResponse.filter(isFullDocumentDetails);
            }),
        enabled: !!productId,
        ...options,
    });
}

export function useProductCards(productId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["cards-product-documents", productId],
        queryFn: () =>
            axiosPriv
                .get(apiUrls.products.cardsProductDocuments(productId))
                .then((response) => z.array(DocumentDetailsSchema).parse(response.data)),
        enabled: !!productId,
        ...options,
    });
}

export function useListCustomerDocuments(customerId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["list-customer-documents", customerId],
        queryFn: () =>
            axiosPriv.get(apiUrls.customers.listCustomerDocuments(customerId)).then((response) => {
                const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
                return parsedResponse.filter(isFullDocumentDetails);
            }),
        enabled: !!customerId,
        ...options,
    });
}

export function useDeleteDocumentFromCustomer(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.deleteDocumentsFromCustomerSales(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["customer-documents", variables.customer_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["customer-documents-with-details", variables.customer_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["document-products", variables.document_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["list-customer-documents", variables.customer_id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useDeleteDocumentFromProduct(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.deleteDocumentFromProduct(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["product-documents", variables.product_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["product-documents-with-details", variables.product_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["document-products", variables.document_id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useUpdateDocumentProducts(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.updateDocumentProducts(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["document-products", variables.document_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["all-product-documents-with-details"],
            });
            for (const productId of variables.product_ids) {
                await queryClient.invalidateQueries({
                    queryKey: ["product-documents", productId],
                });
                await queryClient.invalidateQueries({
                    queryKey: ["product-documents-with-details", productId],
                });
            }
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

// Agreement document hooks
export function useAgreementDocuments(agreementId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        queryKey: ["agreement-documents", agreementId],
        queryFn: () => documentsApi.listAgreementDocuments(axiosPriv, agreementId),
        enabled: !!agreementId,
        ...options,
    });
}

export function useAgreementDocumentsWithDetails(agreementId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["agreement-documents-with-details", agreementId],
        queryFn: () => documentsApi.getAgreementDocumentsWithDetails(axiosPriv, agreementId),
        enabled: !!agreementId,
        ...options,
    });
}

export function useListAgreementDocuments(agreementId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["list-agreement-documents", agreementId],
        queryFn: () =>
            axiosPriv
                .get(apiUrls.agreements.listAgreementDocuments(agreementId))
                .then((response) => {
                    const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
                    return parsedResponse.filter(isFullDocumentDetails);
                }),
        enabled: !!agreementId,
        ...options,
    });
}

export function useDeleteDocumentFromAgreement(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.deleteDocumentsFromAgreementSales(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["agreement-documents", variables.agreement_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["agreement-documents-with-details", variables.agreement_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["list-agreement-documents", variables.agreement_id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}

export function useListChannelDocuments(channelId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["list-channel-documents", channelId],
        queryFn: () =>
            axiosPriv.get(apiUrls.sales.channels.listChannelDocuments(channelId)).then((response) => {
                const parsedResponse = DocumentDetailsArrayResponseSchema.parse(response.data);
                return parsedResponse.filter(isFullDocumentDetails);
            }),
        enabled: !!channelId,
        ...options,
    });
}

export function useDeleteDocumentFromChannel(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        mutationFn: (data) => documentsApi.deleteDocumentsFromChannel(axiosPriv, data),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["channel-documents", variables.channel_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["channel-documents-with-details", variables.channel_id],
            });
            await queryClient.invalidateQueries({
                queryKey: ["list-channel-documents", variables.channel_id],
            });
            options.onSuccess?.(data, variables, context);
        },
        onError: onServerError,
    });
}